import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFileUrl, apiGet, apiPost } from '../../lib/api';
import { TIMEZONE } from '../../lib/format';

/**
 * Conta → Facturare, dinspre client ([#184](https://github.com/bplugaru/efx-pcrm/issues/184)).
 *
 * Schemele sunt scrise aici, nu importate din `@pcrm/be/billing`: `apps/crm` e `scope:fe`.
 * Granița se validează în ambele capete — un câmp redenumit pe server pică la `parse`, nu
 * ajunge `undefined` în listă.
 */

export const totalsSchema = z.object({ net: z.string(), vat: z.string(), payable: z.string() });

export const invoiceSchema = z.object({
  id: z.uuid(),
  companyId: z.string(),
  customerId: z.string(),
  /** Lunile acoperite, în ordine (#210); goală la factura de echipamente și servicii. */
  months: z.array(z.string()),
  series: z.string(),
  number: z.string(),
  /** N-a plecat la ANAF: se mai poate corecta și șterge (#201). */
  editable: z.boolean(),
  issueDate: z.string(),
  dueDate: z.string(),
  currency: z.string(),
  totals: totalsSchema,
  files: z.object({ xml: z.boolean(), pdf: z.boolean(), error: z.string().nullable() }),
});

export type BillingInvoice = z.infer<typeof invoiceSchema>;

export const noteSchema = z.object({ code: z.string(), message: z.string() });

const rowSchema = z.object({
  customerId: z.uuid(),
  nickname: z.string(),
  name: z.string(),
  customerType: z.number(),
  companyId: z.string(),
  state: z.enum(['issued', 'queued', 'ready', 'blocked']),
  autoInvoicing: z.boolean(),
  subscriptions: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      cost: z.string().nullable(),
      currency: z.number(),
      priceIncludesVat: z.boolean(),
    }),
  ),
  totals: totalsSchema.nullable(),
  fingerprint: z.string().nullable(),
  reasons: z.array(noteSchema),
  markers: z.array(noteSchema),
  invoice: invoiceSchema.nullable(),
  batchStatus: z.enum(['queued', 'processing']).nullable(),
  lastSkip: z.object({ reason: z.string(), at: z.string() }).nullable(),
});

export type BillingRow = z.infer<typeof rowSchema>;
export type BillingRowState = BillingRow['state'];

const monthViewSchema = z.object({
  month: z.string(),
  monthLabel: z.string(),
  issueDate: z.string(),
  vatRate: z.object({ value: z.string().nullable(), problem: z.string().nullable() }),
  /** Cursurile EUR și USD, cu ziua lor și ce le împiedică azi (#185). */
  exchangeRates: z
    .array(
      z.object({
        currency: z.string(),
        setting: z.string(),
        value: z.string().nullable(),
        date: z.string().nullable(),
        problem: z.string().nullable(),
      }),
    )
    .default([]),
  /**
   * `'13:00'` cât cursul urmează să se schimbe azi, altfel `null` (#214). Opțional la `parse`: un
   * server nedeployat încă nu-l trimite, iar ecranul nu trebuie să cadă din cauza asta.
   */
  exchangeRateUpdateAt: z.string().nullable().default(null),
  companies: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      shortName: z.string().nullable(),
      series: z.object({ code: z.string(), nextNumber: z.string() }).nullable(),
    }),
  ),
  rows: z.array(rowSchema),
  batches: z.array(
    z.object({
      id: z.string(),
      issueDate: z.string(),
      createdAt: z.string(),
      createdByName: z.string().nullable(),
      queued: z.number(),
      processing: z.number(),
      issued: z.number(),
      skipped: z.number(),
    }),
  ),
});

export type BillingMonthView = z.infer<typeof monthViewSchema>;

export const BILLING_QUERY_KEY = ['billing'] as const;

/**
 * Cât de des se reîncarcă lista cât lucrează jobul: rândurile trec în „Emisă" sub ochii
 * operatorului, fără să apese nimic. Același ritm ca bătaia jobului.
 */
const LIVE_REFRESH_MS = 3_000;

/**
 * Lista lunii. Se reîncarcă singură **doar cât e ceva în mișcare**: un lot cu rânduri în
 * așteptare, sau facturi emise ale căror fișiere încă se fac. O listă liniștită nu bate la
 * server la fiecare trei secunde.
 */
export const billingMonthQueryOptions = (month: string, companyId: string | undefined) =>
  queryOptions({
    queryKey: [...BILLING_QUERY_KEY, month, companyId ?? null] as const,
    queryFn: () =>
      apiGet(
        `/billing/months/${month}${companyId ? `?companyId=${encodeURIComponent(companyId)}` : ''}`,
        monthViewSchema,
      ),
    refetchInterval: (query) => (isInMotion(query.state.data) ? LIVE_REFRESH_MS : false),
  });

function isInMotion(view: BillingMonthView | undefined): boolean {
  if (view === undefined) {
    return false;
  }

  return (
    view.batches.some((batch) => batch.queued + batch.processing > 0) ||
    view.rows.some(
      (row) =>
        row.state === 'queued' ||
        // Fișierele se fac după emitere; cât lipsesc și nu s-a scris încă de ce, sunt pe drum.
        (row.invoice !== null &&
          (!row.invoice.files.pdf || !row.invoice.files.xml) &&
          row.invoice.files.error === null),
    )
  );
}

export function startInvoiceBatch(input: {
  month: string;
  rows: readonly { customerId: string; fingerprint: string }[];
}) {
  return apiPost(
    '/billing/batches',
    input,
    z.object({
      batch: z.object({
        batchId: z.string(),
        month: z.string(),
        issueDate: z.string(),
        count: z.number(),
      }),
    }),
  ).then((body) => body.batch);
}

export function previewInvoiceHtml(month: string, customerId: string): Promise<string> {
  return apiPost(
    `/billing/months/${month}/preview`,
    { customerId },
    z.object({ html: z.string() }),
  ).then((body) => body.html);
}

/**
 * Șterge facturi netrimise la ANAF (#201). Contorul seriei revine la cel mai mare număr rămas.
 */
export function deleteInvoices(invoiceIds: readonly string[]): Promise<number> {
  return apiPost(
    '/billing/invoices/delete',
    { invoiceIds },
    z.object({ deleted: z.number() }),
  ).then((body) => body.deleted);
}

export function invoiceFileUrl(invoiceId: string, kind: 'pdf' | 'xml'): string {
  return apiFileUrl(`/invoices/${invoiceId}/${kind}`);
}

/**
 * Luna de azi, **la București**: `2026-10`. E doar punctul de plecare al selectorului.
 *
 * Nu `new Date().toISOString().slice(0, 7)`: pe 1 noiembrie la 01:00, ora României, aia ar da
 * încă octombrie.
 */
export function currentBillingMonth(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${value('year')}-${value('month')}`;
}

/** `2026-10` cu `delta` luni: `-1` → `2026-09`. */
export function shiftMonth(month: string, delta: number): string {
  const [year = 0, monthNumber = 1] = month.split('-').map(Number);
  const index = year * 12 + (monthNumber - 1) + delta;

  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

const MONTH_NAMES = [
  'ianuarie',
  'februarie',
  'martie',
  'aprilie',
  'mai',
  'iunie',
  'iulie',
  'august',
  'septembrie',
  'octombrie',
  'noiembrie',
  'decembrie',
] as const;

/** `2026-10` → `octombrie 2026`. */
export function billingMonthLabel(month: string): string {
  const [year, monthNumber = 1] = month.split('-');

  return `${MONTH_NAMES[Number(monthNumber) - 1] ?? month} ${year ?? ''}`.trim();
}

const SHORT_MONTH_NAMES = [
  'ian.',
  'feb.',
  'mar.',
  'apr.',
  'mai',
  'iun.',
  'iul.',
  'aug.',
  'sept.',
  'oct.',
  'nov.',
  'dec.',
] as const;

/**
 * Lunile unei facturi, de citit (#210) — aceeași formă ca `monthsLabel` de pe server: o lună
 * întreagă (`octombrie 2026`), un interval când sunt legate (`oct. – dec. 2026`,
 * `nov. 2026 – feb. 2027`), o listă când nu (`ian., mar. 2027`).
 */
export function billingMonthsLabel(months: readonly string[]): string {
  const sorted = [...new Set(months)].sort();
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  if (first === undefined || last === undefined) {
    return '';
  }

  if (sorted.length === 1) {
    return billingMonthLabel(first);
  }

  const year = (month: string) => month.slice(0, 4);
  const short = (month: string) => SHORT_MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month;
  const consecutive = sorted.every(
    (month, index) => index === 0 || month === shiftMonth(sorted[index - 1] ?? month, 1),
  );

  if (consecutive) {
    return year(first) === year(last)
      ? `${short(first)} – ${short(last)} ${year(last)}`
      : `${short(first)} ${year(first)} – ${short(last)} ${year(last)}`;
  }

  const byYear = new Map<string, string[]>();

  for (const month of sorted) {
    byYear.set(year(month), [...(byYear.get(year(month)) ?? []), short(month)]);
  }

  return [...byYear].map(([key, names]) => `${names.join(', ')} ${key}`).join(', ');
}

/**
 * Suma unor totaluri, pentru confirmare: `"100.01"` + `"424.11"` = `"524.12"`.
 *
 * ⚠️ **Fără `number`**: 0,1 + 0,2 în virgulă mobilă e 0,30000000000000004. Sumele vin de la
 * server cu exact două zecimale, deci se adună în bani, ca întregi.
 */
export function sumAmounts(values: readonly string[]): string {
  const cents = values.reduce((total, value) => {
    const [whole = '0', fraction = ''] = value.split('.');

    return total + BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0').slice(0, 2));
  }, 0n);

  const text = cents.toString().padStart(3, '0');

  return `${text.slice(0, -2)}.${text.slice(-2)}`;
}
